import React, { useMemo } from "react";
import {
  ABC_ROW_HEADERS,
  ANALYSIS_TYPE_LABELS,
  ANALYSIS_TYPE_VARIANT,
  XYZ_COLUMN_LABELS,
} from "../constants";
import { computeCellIntensities } from "../utils/cellIntensity";
import ABCXYZMatrixCell from "./ABCXYZMatrixCell";
import ABCXYZInfoTooltip from "./ABCXYZInfoTooltip";

/**
 * The 3x3 ABC/XYZ matrix.
 *
 * Rows are the impact axis (A/B/C), columns the stability axis (X/Y/Z); the
 * backend delivers the cells in fixed A→C × X→Z order and they are laid out
 * positionally. Both axis label sets share one typography treatment, and the
 * label strips mirror the cell grid's track definition (3 × 1fr + same gap),
 * so alignment holds at every tile size.
 */
const ABCXYZMatrix = ({
  matrixCells,
  mode,
  onCellClick,
  groupByAttribute = null,
  objectType = null,
}) => {
  // Percentage-driven color intensity, normalized within each XYZ family
  // (the hue keeps its category meaning; the share sets the shade strength).
  const intensities = useMemo(
    () => computeCellIntensities(matrixCells || []),
    [matrixCells],
  );

  if (!matrixCells || matrixCells.length !== 9) return null;

  const isVariant = mode === ANALYSIS_TYPE_VARIANT;

  const cellConceptLines =
    !isVariant && groupByAttribute && objectType
      ? [
          {
            label: "Cell numbers",
            text:
              `the main number is how many "${groupByAttribute}" groups ` +
              `fall in that class; the smaller number is how many ` +
              `underlying "${objectType}" objects belong to those groups.`,
          },
        ]
      : null;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-sm font-semibold text-gray-800">
          {ANALYSIS_TYPE_LABELS[mode]}
        </span>
        {/* CV is explained on the Avg. Variability card. */}
        <ABCXYZInfoTooltip
          title={ANALYSIS_TYPE_LABELS[mode]}
          sections={[isVariant ? "variantMatrix" : "objectMatrix", "abc", "xyz"]}
          extraLines={cellConceptLines}
        />
      </div>

      <div className="grid grid-cols-[auto_1fr] grid-rows-[auto_1fr] max-w-5xl flex-1 min-h-0">
        <div />

        {/* XYZ column labels — letter first, name in parentheses, the one
            axis-label convention used by every matrix in the tile. */}
        <div className="grid grid-cols-3 gap-1.5 pb-2 pl-2">
          {["X", "Y", "Z"].map((xyz) => (
            <div key={xyz} className="flex items-center justify-center">
              <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                {xyz} ({XYZ_COLUMN_LABELS[xyz]})
              </span>
            </div>
          ))}
        </div>

        {/* ABC row labels */}
        <div className="grid grid-rows-3 gap-1.5 pr-3 pt-2 h-full">
          {["A", "B", "C"].map((abc) => (
            <div key={abc} className="flex items-center">
              <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                {abc} ({ABC_ROW_HEADERS[abc][isVariant ? "variant" : "object"]})
              </span>
            </div>
          ))}
        </div>

        {/* Cells — track definition mirrors the label strips exactly. */}
        <div className="grid grid-cols-3 grid-rows-3 gap-1.5 pt-2 pl-2 h-full">
          {matrixCells.map((cell) => (
            <ABCXYZMatrixCell
              key={cell.abc_xyz_class}
              cell={cell}
              mode={mode}
              intensity={intensities[cell.abc_xyz_class] || 0}
              onCellClick={onCellClick}
              groupByAttribute={groupByAttribute}
              objectType={objectType}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ABCXYZMatrix;
